import { FC, useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'react-daisyui';
import PageHeader from '@/components/ui/PageHeader/PageHeader.tsx';
import usePaperAttempt from '@/api/usePaperAttempt.ts';
import usePaperQuestions from '@/api/usePaperQuestions.ts';
import useAttemptAnswers from '@/api/useAttemptAnswers.ts';
import usePastPaper from '@/api/usePastPaper.ts';
import { useAuthContext } from '@/context/AuthContext';
import useCreateAttempt from '@/api/useCreateAttempt';
import supabaseClient from '@/api/supabaseClient.ts';
import { useQueryClient } from '@tanstack/react-query';
import Routes from '@/router/Routes';
import AnswerFeedback from '@/components/ui/AnswerFeedback/AnswerFeedback.tsx';
import DailyLimitModal from '@/components/ui/DailyLimitModal/DailyLimitModal';
import AreasToImproveCard from '@/components/ui/AreasToImproveCard/AreasToImproveCard';

// Reusable Question Card Component
interface QuestionCardProps {
  questionNumber: number;
  score: number;
  maxScore: number;
  onClick?: () => void;
  isSectionB?: boolean;
  isGrading?: boolean;
}

const QuestionCard: FC<QuestionCardProps> = ({ questionNumber, score, maxScore, onClick, isSectionB, isGrading }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full p-6 bg-white rounded-[20px] border-[0.72px] border-[#CCCCCC] border-opacity-50 text-left ${
        onClick ? 'hover:shadow-md transition-shadow' : 'cursor-default'
      }`}
    >
      <span className="text-lg font-medium flex gap-4">
        <span className="text-text-primary">{isSectionB ? 'Section B - ' : `Question ${questionNumber} - `}</span>
        {isGrading ? (
          <span className="text-gray-500 flex items-center gap-2 pl-1">
            <svg
              className="animate-spin h-4 w-4 text-orange-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Grading...
          </span>
        ) : (
          <span className="text-orange-600">
            {score}/{maxScore}
          </span>
        )}
        {!isGrading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Click to view feedback</span>
            <svg
              className="w-5 h-5 text-orange-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        )}
      </span>
    </button>
  );
};

const AttemptResultsPage: FC = () => {
  const { attemptId } = useParams<{ attemptId: string }>();
  const navigate = useNavigate();
  const { session } = useAuthContext();
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<number | null>(null);
  const [limitError, setLimitError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch the attempt
  const { data: attempt, isLoading: isLoadingAttempt } = usePaperAttempt(attemptId);

  // Fetch questions for this paper
  const { data: questions = [], isLoading: isLoadingQuestions } = usePaperQuestions(attempt?.past_paper_id);

  // Fetch attempt answers
  const { data: attemptAnswers = [], isLoading: isLoadingAnswers } = useAttemptAnswers(attemptId);

  // Fetch past paper for reading insert content
  const { data: pastPaper } = usePastPaper(attempt?.past_paper_id);

  // Create attempt mutation
  const createAttemptMutation = useCreateAttempt();

  // Force refetch on mount to ensure fresh data
  useEffect(() => {
    if (attemptId) {
      console.log('Forcing refetch of attempt data on mount');
      queryClient.invalidateQueries({ queryKey: ['paper_attempt', attemptId] });
      queryClient.invalidateQueries({ queryKey: ['past_paper_attempt_answers', attemptId] });
    }
  }, [attemptId, queryClient]);

  // Subscribe to Realtime updates for grading progress + polling fallback
  useEffect(() => {
    if (!attemptId || !attempt) return;

    console.log('Setting up Realtime subscriptions for attempt:', attemptId);

    // Subscribe to changes in past_paper_attempt_answers (individual question grading)
    const answersChannel = supabaseClient
      .channel(`past_paper_attempt_answers:${attemptId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'past_paper_attempt_answers',
          filter: `attempt_id=eq.${attemptId}`,
        },
        (payload) => {
          console.log('🔔 Answer graded (Realtime):', payload);
          // Invalidate answers query to refetch
          queryClient.invalidateQueries({ queryKey: ['past_paper_attempt_answers', attemptId] });
        }
      )
      .subscribe((status) => {
        console.log('Answers channel subscription status:', status);
      });

    // Subscribe to changes in past_paper_attempts (status changes)
    const attemptChannel = supabaseClient
      .channel(`past_paper_attempts:${attemptId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'past_paper_attempts',
          filter: `id=eq.${attemptId}`,
        },
        (payload) => {
          console.log('🔔 Attempt updated (Realtime):', payload);
          // Invalidate attempt query to refetch
          queryClient.invalidateQueries({ queryKey: ['paper_attempt', attemptId] });
        }
      )
      .subscribe((status) => {
        console.log('Attempt channel subscription status:', status);
      });

    // Set up polling fallback when grading is in progress
    // This ensures we catch updates even if Realtime fails
    let pollingInterval: NodeJS.Timeout | null = null;

    if (attempt.status === 'in_progress' || attempt.status === 'grading') {
      console.log('🔄 Starting polling fallback for grading updates (every 3 seconds)');
      pollingInterval = setInterval(() => {
        console.log('⏰ Polling for grading updates...');
        queryClient.invalidateQueries({ queryKey: ['past_paper_attempt_answers', attemptId] });
        queryClient.invalidateQueries({ queryKey: ['paper_attempt', attemptId] });
      }, 3000); // Poll every 3 seconds while grading
    }

    // Cleanup subscriptions on unmount
    return () => {
      console.log('Cleaning up Realtime subscriptions and polling');
      supabaseClient.removeChannel(answersChannel);
      supabaseClient.removeChannel(attemptChannel);
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [attemptId, attempt, queryClient]);

  const handleBack = () => {
    if (selectedQuestionIndex !== null) {
      setSelectedQuestionIndex(null);
    } else {
      navigate(-1);
    }
  };

  const isLoading = isLoadingAttempt || isLoadingQuestions || isLoadingAnswers;

  // Create a map of question_id to answer for easy lookup
  const answersMap = useMemo(() => {
    const map = new Map();
    attemptAnswers.forEach((answer) => {
      map.set(answer.question_id, answer);
    });
    return map;
  }, [attemptAnswers]);

  // Group questions for display: Section A questions + Section B as one item
  const displayQuestions = useMemo(() => {
    const sectionAQuestions = questions.filter((q) => q.category !== 'SectionB');
    const sectionBQuestions = questions.filter((q) => q.category === 'SectionB');

    // Find which Section B question was graded (could be answered or unanswered with 0 score)
    // Priority: 1) Graded with content, 2) Graded without content (0 score), 3) First one
    const gradedSectionBQuestion =
      sectionBQuestions.find((q) => {
        const answer = answersMap.get(q.id);
        return answer && answer.graded_at;
      }) || sectionBQuestions[0]; // Fallback to first if none graded yet

    return {
      sectionA: sectionAQuestions,
      sectionB: {
        questions: sectionBQuestions,
        answeredQuestion: gradedSectionBQuestion, // Keeping property name for compatibility
      },
    };
  }, [questions, answersMap]);

  // Calculate total score and max score
  const { totalScore, maxScore } = useMemo(() => {
    let total = 0;
    let max = 0;

    // Add Section A scores
    displayQuestions.sectionA.forEach((question) => {
      const answer = answersMap.get(question.id);
      total += answer?.ai_score || 0;
      max += question.marks || 0;
    });

    // Add Section B score (only the answered question)
    if (displayQuestions.sectionB.answeredQuestion) {
      const answer = answersMap.get(displayQuestions.sectionB.answeredQuestion.id);
      total += answer?.ai_score || 0;
      max += displayQuestions.sectionB.answeredQuestion.marks || 0;
    }

    return { totalScore: total, maxScore: max };
  }, [displayQuestions, answersMap]);

  // Analyze performance by category and identify weak areas
  const weakCategoriesAnalysis = useMemo(() => {
    const categoryScores: Record<
      string,
      {
        totalScore: number;
        maxScore: number;
        count: number;
      }
    > = {};

    // Analyze Section A questions
    displayQuestions.sectionA.forEach((question) => {
      const answer = answersMap.get(question.id);
      if (!answer || !question.category) return;

      const category = question.category;

      if (!categoryScores[category]) {
        categoryScores[category] = { totalScore: 0, maxScore: 0, count: 0 };
      }

      categoryScores[category].totalScore += answer.ai_score || 0;
      categoryScores[category].maxScore += question.marks || 0;
      categoryScores[category].count++;
    });

    // Analyze Section B question - use 'SectionB' as the category
    if (displayQuestions.sectionB.answeredQuestion) {
      const question = displayQuestions.sectionB.answeredQuestion;
      const answer = answersMap.get(question.id);

      if (answer) {
        const category = 'SectionB';

        if (!categoryScores[category]) {
          categoryScores[category] = { totalScore: 0, maxScore: 0, count: 0 };
        }

        categoryScores[category].totalScore += answer.ai_score || 0;
        categoryScores[category].maxScore += question.marks || 0;
        categoryScores[category].count++;
      }
    }

    // Calculate percentages and identify weak areas
    const allCategories = Object.entries(categoryScores)
      .map(([category, scores]) => ({
        category,
        percentage: scores.maxScore > 0 ? (scores.totalScore / scores.maxScore) * 100 : 0,
        totalScore: scores.totalScore,
        maxScore: scores.maxScore,
        count: scores.count,
      }))
      .sort((a, b) => a.percentage - b.percentage); // Worst first

    // Weak if <70%
    const weakCategories = allCategories.filter((cat) => cat.percentage < 70);

    return {
      weakCategories,
      allCategories,
    };
  }, [displayQuestions, answersMap]);

  // Get the selected question's answer data
  const selectedQuestionAnswer = useMemo(() => {
    if (selectedQuestionIndex === null) return null;

    // If it's a Section A question
    if (selectedQuestionIndex < displayQuestions.sectionA.length) {
      const question = displayQuestions.sectionA[selectedQuestionIndex];
      return question ? answersMap.get(question.id) : null;
    }

    // If it's Section B
    if (displayQuestions.sectionB.answeredQuestion) {
      return answersMap.get(displayQuestions.sectionB.answeredQuestion.id);
    }

    return null;
  }, [selectedQuestionIndex, displayQuestions, answersMap]);

  // Get the selected question for display
  const selectedQuestion = useMemo(() => {
    if (selectedQuestionIndex === null) return null;

    // If it's a Section A question
    if (selectedQuestionIndex < displayQuestions.sectionA.length) {
      return displayQuestions.sectionA[selectedQuestionIndex];
    }

    // If it's Section B
    return displayQuestions.sectionB.answeredQuestion;
  }, [selectedQuestionIndex, displayQuestions]);

  const handleStartPaper = async () => {
    if (!session?.user?.id || !attempt) return;

    setLimitError(null); // Clear previous errors

    try {
      const createdAttempt = await createAttemptMutation.mutateAsync({
        authId: session.user.id,
        pastPaperId: attempt?.past_paper_id,
      });

      // Navigate to take paper page
      navigate(`/paper-attempts/${createdAttempt.id}`);
    } catch (error: unknown) {
      console.error('Failed to create attempt:', error);

      // Extract error message
      const errorMessage = error instanceof Error ? error.message : 'Failed to create paper attempt. Please try again.';

      setLimitError(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <>
        <PageHeader title="Loading..." showBackButton />
        <div className="flex items-center justify-center flex-1">
          <p className="text-gray-500">Loading results...</p>
        </div>
      </>
    );
  }

  if (!attempt) {
    return (
      <>
        <PageHeader title="Attempt Not Found" showBackButton />
        <div className="flex items-center justify-center flex-1">
          <p className="text-gray-500">This attempt does not exist.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Summary" showBackButton onBackClick={handleBack} titleAlign="left"></PageHeader>
      <div className="flex gap-4 justify-end">
        {selectedQuestionIndex === null ? (
          <>
            <Button
              size="md"
              onClick={handleStartPaper}
              className="bg-orange-500 hover:bg-orange-600 border-none text-white px-8"
            >
              Retry this paper
            </Button>
            <Button
              size="md"
              className="bg-[#2B3440] hover:bg-dark-hover-secondary border-none text-white px-8"
              onClick={() => navigate(Routes.FULL_PAST_PAPERS)}
            >
              Finish
            </Button>
          </>
        ) : (
          <Button
            size="md"
            className="bg-[#2B3440] hover:bg-dark-hover-secondary border-none text-white px-8"
            onClick={() => setSelectedQuestionIndex(null)}
          >
            Summary
          </Button>
        )}
      </div>
      <div className="flex-1 flex flex-col gap-6 overflow-hidden">
        {selectedQuestionIndex === null ? (
          /* Summary View */
          <div className="flex-1 flex flex-col gap-5 p-6 overflow-y-auto">
            {/* Questions List */}
            <div className="space-y-5">
              {/* Section A Questions */}
              {displayQuestions.sectionA.map((question, index) => {
                const answer = answersMap.get(question.id);
                const isGrading = attempt?.status === 'grading' && !answer?.graded_at;
                return (
                  <QuestionCard
                    key={question.id}
                    questionNumber={index + 1}
                    score={answer?.ai_score || 0}
                    maxScore={question.marks!}
                    onClick={() => setSelectedQuestionIndex(index)}
                    isGrading={isGrading}
                  />
                );
              })}

              {/* Section B - Single card showing answered question */}
              {displayQuestions.sectionB.answeredQuestion &&
                (() => {
                  const answer = answersMap.get(displayQuestions.sectionB.answeredQuestion.id);
                  const isGrading = attempt?.status === 'grading' && !answer?.graded_at;
                  return (
                    <QuestionCard
                      key="section-b"
                      questionNumber={displayQuestions.sectionA.length + 1}
                      score={answer?.ai_score || 0}
                      maxScore={displayQuestions.sectionB.answeredQuestion.marks!}
                      onClick={() => setSelectedQuestionIndex(displayQuestions.sectionA.length)}
                      isSectionB
                      isGrading={isGrading}
                    />
                  );
                })()}
            </div>

            {/* Final Grade */}
            {attempt?.status === 'grading' ? (
              <div className="bg-gray-400 text-white rounded-[20px] p-6 border-t-[0.72px] border-b-[0.72px] border-[#CCCCCC] border-opacity-50">
                <h2 className="text-2xl font-bold">Final Grade – Calculating...</h2>
              </div>
            ) : (
              <div className="bg-orange-500 text-white rounded-[20px] p-6 border-t-[0.72px] border-b-[0.72px] border-[#CCCCCC] border-opacity-50">
                <h2 className="text-2xl font-bold">
                  Final Grade – {totalScore}/{maxScore}
                </h2>
              </div>
            )}

            {/* What's Next? Call to Action */}
            {attempt?.status !== 'grading' && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-[20px] p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">What's Next?</h3>
                    <p className="text-gray-700 mb-3">
                      You've completed this paper and received your grade. Well done!
                    </p>
                    {totalScore / maxScore < 0.7 ? (
                      <p className="text-gray-700">
                        Want to improve your score? Visit the{' '}
                        <span className="font-semibold text-blue-600">Practice Area</span> section to focus on specific
                        question types where you need more practice. Each question type has targeted exercises to help
                        you master the skills needed.
                      </p>
                    ) : (
                      <p className="text-gray-700">
                        Great job! You scored {Math.round((totalScore / maxScore) * 100)}%! You can retry this paper,
                        try another paper or return to the practice area to perfect your answers
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Areas to Improve - AI-Generated Practice Set */}
            {attempt?.status !== 'grading' && (
              <AreasToImproveCard
                weakCategories={weakCategoriesAnalysis.weakCategories}
                subject={pastPaper?.subject}
                paperNumber={pastPaper?.paper_number}
              />
            )}
          </div>
        ) : (
          /* Question Detail View */
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Question Header */}
              <QuestionCard
                questionNumber={selectedQuestionIndex + 1}
                score={selectedQuestionAnswer?.ai_score || 0}
                maxScore={selectedQuestion?.marks || 0}
              />

              <AnswerFeedback
                answerText={selectedQuestionAnswer?.answer_text}
                aiFeedback={selectedQuestionAnswer?.ai_feedback}
                aiStrengths={selectedQuestionAnswer?.ai_strengths}
                aiImprovements={selectedQuestionAnswer?.ai_improvements}
                aiScore={selectedQuestionAnswer?.ai_score || undefined}
                maxScore={selectedQuestion?.marks || 0}
                gradedAt={selectedQuestionAnswer?.graded_at}
                questionContent={selectedQuestion?.content}
                readingInsertContent={pastPaper?.reading_insert_content}
                readingInsertPageNumbers={pastPaper?.reading_insert_page_numbers}
                pdfUrl={
                  pastPaper?.pdf_url
                    ? supabaseClient.storage.from('past-papers').getPublicUrl(pastPaper.pdf_url).data.publicUrl
                    : null
                }
                imagePageNumbers={selectedQuestion?.image_page_numbers}
                imagePdfUrl={
                  pastPaper?.pdf_url
                    ? supabaseClient.storage.from('past-papers').getPublicUrl(pastPaper.pdf_url).data.publicUrl
                    : null
                }
              />
            </div>
          </div>
        )}
      </div>

      {/* Usage Limit Error Modal */}
      {limitError && <DailyLimitModal errorMessage={limitError} onClose={() => setLimitError(null)} />}
    </>
  );
};

export default AttemptResultsPage;
